import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { EventManager } from '@angular/platform-browser';
import { Observable, Subject, throwError } from 'rxjs';

import { take, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { HotkeysPopupComponent } from './hotkeys-popup/hotkeys-popup.component';
import { GtagService } from '../../../core/gx-gtag/gtag.service';
import { S } from '@angular/cdk/keycodes';
import { getFromLocalStorage, setToLocalStorage } from '../../../core/utilities';

export type HotkeyArtefact = {
  action: string;
  dispose: () => void
}

// inspired by https://netbasal.com/diy-keyboard-shortcuts-in-your-angular-application-4704734547a2
@Injectable({
  providedIn: 'root',
})
export class HotkeysService {
  private hotkeys = new Map<string, HotkeyArtefact>();
  private defaultElement: HTMLElement = this.document.documentElement;
  private hotkeysEnabled = false;
  private readonly hotkeysPopupHotkey = 'shift.?';
  private readonly hotkeysEnabledLocalStorageKey = 'HOTKEYS_ENABLED';

  // The hotkeysPopupHotkey will be registered automatically as the first hotkey when
  // addHotkeyAndGetEventFeed() is called for the first time. Remove it by iterating this Subject.
  private removeHotkeysPopupHotkey = new Subject<void>();

  constructor(private eventManager: EventManager,
              private dialog: MatDialog,
              private gtag: GtagService,
              @Inject(DOCUMENT) private document: Document) {
    if (!getFromLocalStorage(this.hotkeysEnabledLocalStorageKey)) {
      setToLocalStorage(this.hotkeysEnabledLocalStorageKey, this.hotkeysEnabled);
    } else {
      this.hotkeysEnabled = getFromLocalStorage(this.hotkeysEnabledLocalStorageKey);
    }

    // Prevent the default 'save as' function upon pressing ctrl+s which is being used to save an
    // BLS location - 'save as' is not really being used, so it's fine to keep the listener
    this.eventManager.addEventListener(this.document.documentElement, 'keydown',
        (e) => {
          if ((e.ctrlKey || e.metaKey) && e.keyCode === S) {
            e.preventDefault();
          }
        });
  }

  /**
   * Open hotkeys popup
   */
  openHotkeysPopup(): void {
    this.dialog.open(HotkeysPopupComponent, {
      data: {
        hotkeysEnabled: this.hotkeysEnabled,
        hotkeysMap: this.hotkeys,
        hotkeysPopupHotkey: this.hotkeysPopupHotkey,
      },
      height: '720px',
    }).afterClosed()
        .subscribe((hotkeysEnabled) => {
          if (typeof hotkeysEnabled === 'boolean') { // only 'save' action returns a boolean value
            this.hotkeysEnabled = hotkeysEnabled;
            setToLocalStorage(
                this.hotkeysEnabledLocalStorageKey, this.hotkeysEnabled);
          }
        });
    // track event
    this.gtag.event(
        'See shortcuts',
        {
          event_category: 'shortcut action',
          event_label: 'See shortcuts',
        },
    );
  }

  /**
   * Register a hotkey and return a keyboard event Observable. If this is the first  hotkey being
   * registered, register a 'See shortcuts' hotkey as well, which will be removed when it's the last
   * hotkey in store.
   * @param hotkey hotkey string
   * @param action action description
   * @param componentDestroyed notifier - needed for removal of event listeners
   * @returns Observable<KeyboardEvent> which will emit a keyboard event on pressing the registered hotkey
   */
  addHotkeyAndGetEventFeed(
      hotkey: string,
      action: string,
      componentDestroyed: Subject<void>): Observable<KeyboardEvent> {
    if (!this.hotkeys.has(this.hotkeysPopupHotkey)) {
      this.add(this.hotkeysPopupHotkey, 'See shortcuts', this.removeHotkeysPopupHotkey)
          .pipe(takeUntil(this.removeHotkeysPopupHotkey))
          .subscribe(() => {
            this.openHotkeysPopup();
          });
    }

    return this.add(hotkey, action, componentDestroyed);
  }

  /**
   * Register a hotkey and return a keyboard event Observable.
   * @param hotkey hotkey string
   * @param action action description
   * @param componentDestroyed notifier - needed for removal of event listeners
   * @returns Observable which will emit a keyboard event on pressing the registered hotkey
   * @private
   */
  private add(hotkey: string, action: string, componentDestroyed: Subject<void>): Observable<any> {
    const event = `keydown.${hotkey}`;
    const hotkeyArtefact: HotkeyArtefact = {
      action: action,
      dispose: null,
    };

    if (!this.hotkeys.has(hotkey)) {
      return new Observable(observer => {
        const handler = (e: KeyboardEvent) => {

          const hotkeysAllowedInTextfield = ['control.enter', 'control.s'];
          const hotkeyFiredInTextfield = (e.target['tagName'].toLowerCase() === 'textarea' ||
              (e.target['tagName'].toLowerCase() === 'input' &&
                  (e.target['type'].toLowerCase() === 'text' ||
                      e.target['type'].toLowerCase() === 'number')));

          // filter out these event sources
          if (this.hotkeysEnabled && (hotkeysAllowedInTextfield.includes(hotkey) || !hotkeyFiredInTextfield)
              && (!this.dialog.openDialogs?.length) // suppress hotkeys while any dialog is open
          ) {
            observer.next(e);
          }
        };

        const removeListener = this.eventManager.addEventListener(this.defaultElement, event, handler);
        hotkeyArtefact.dispose = () => {
          removeListener();
          this.hotkeys.delete(hotkey);
          // If there is just one hotkey, it's the hotkeysPopupHotkey as it has been added automatically
          // when addHotkeyAndGetEventFeed() was called for the first time and it needs to be removed
          // as well when there are no other hotkeys left.
          if (this.getHotkeysLength() === 1) {
            this.removeHotkeysPopupHotkey.next();
          }
        };

        this.hotkeys.set(hotkey, hotkeyArtefact);

        componentDestroyed.pipe(take(1))
            .subscribe(() => hotkeyArtefact.dispose());
      });
    } else {
      return throwError(`Cannot register duplicate hotkey (${hotkey})\n`
          + `Registered Entry:\n`
          + `Hotkey: ${hotkey}\n`
          + `Description: ${this.hotkeys.get(hotkey).action}`);
    }
  }

  /**
   * Get the amount of registered hotkeys
   */
  getHotkeysLength(): number {
    return Array.from(this.hotkeys.keys()).length;
  }

}

